import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { ApiClientError } from '@riskpay/ui-components';
import { AppShell, type Route } from './components/AppShell';
import { SignIn } from './screens/SignIn';
import { SignUp } from './screens/SignUp';
import { ForgotPassword } from './screens/ForgotPassword';
import { Dashboard } from './screens/Dashboard';
import { Transactions } from './screens/Transactions';
import { Settlements } from './screens/Settlements';
import { SettlementAccounts } from './screens/SettlementAccounts';
import { Disputes } from './screens/Disputes';
import { Payouts } from './screens/Payouts';
import { Developers } from './screens/Developers';
import { Onboarding } from './screens/Onboarding';
import { api, useAuth } from './lib/client';

/**
 * A 401 means the session is already gone — retrying just burns requests and
 * delays the redirect to sign-in. Everything else gets two attempts.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) =>
        error instanceof ApiClientError && error.status === 401 ? false : failureCount < 2,
    },
  },
});

function Portal() {
  const [route, setRoute] = useState<Route>('dashboard');

  // Pulled once at the shell level: the KYC state drives the sidebar badge and
  // the live-mode banner, so every screen would otherwise refetch it.
  const kyc = useQuery({
    queryKey: ['kyc', 'status'],
    queryFn: () => api.get<{ merchant: { kyc_status: string } }>('/kyc'),
    staleTime: 120_000,
  });

  return (
    <AppShell route={route} onNavigate={setRoute} kycStatus={kyc.data?.merchant.kyc_status}>
      {route === 'dashboard' && <Dashboard onViewAll={() => setRoute('transactions')} />}
      {route === 'transactions' && <Transactions />}
      {route === 'settlements' && <Settlements />}
      {route === 'payouts' && <Payouts />}
      {route === 'settlement-accounts' && <SettlementAccounts />}
      {route === 'disputes' && <Disputes />}
      {route === 'developers' && <Developers />}
      {route === 'onboarding' && <Onboarding />}
    </AppShell>
  );
}

type PublicRoute = 'signin' | 'signup' | 'forgot';

/** Maps the address bar onto the three unauthenticated screens. */
function routeFromPath(pathname: string): PublicRoute {
  if (pathname.startsWith('/signup')) return 'signup';
  if (pathname.startsWith('/forgot-password')) return 'forgot';
  return 'signin';
}

export function App() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const [route, setRoute] = useState<PublicRoute>(() => routeFromPath(window.location.pathname));

  // Keep the address bar and the rendered screen in step, so the browser's
  // back button behaves and a /signup link from the marketing site lands in
  // the right place.
  useEffect(() => {
    const onPop = () => setRoute(routeFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = (to: PublicRoute) => {
    const path = to === 'signup' ? '/signup' : to === 'forgot' ? '/forgot-password' : '/';
    window.history.pushState({}, '', path);
    setRoute(to);
  };

  return (
    <QueryClientProvider client={queryClient}>
      {isAuthenticated ? (
        <Portal />
      ) : route === 'signup' ? (
        <SignUp onNavigate={go} />
      ) : route === 'forgot' ? (
        <ForgotPassword onNavigate={go} />
      ) : (
        <SignIn onNavigate={go} />
      )}
    </QueryClientProvider>
  );
}
